/**
 * Agile read service.
 *
 * Direct Supabase queries for the Agile Delivery domain (work items, boards,
 * sprints, program increments, links, activity, metric snapshots). Converts
 * snake_case DB columns to the camelCase app types. This is the Supabase layer
 * for the agile domain; dataService.ts wraps these with the mock/api/supabase
 * funnel so components call through dataService.
 *
 * Part of the Hybrid Waterfall/Agile Delivery feature (P0 Foundation).
 */

import { supabase } from '@/lib/supabase'
import {
  deriveWorkItemKey,
  type WorkItem,
  type WorkItemType,
  type StatusCategory,
  type Priority,
  type Moscow,
  type EvCreditRule,
  type Board,
  type BoardColumn,
  type Sprint,
  type SprintCapacity,
  type ProgramIncrement,
  type WorkItemLink,
  type WorkItemComment,
  type WorkItemActivity,
  type AgileMetricSnapshot,
} from '@/types/agile'

// ── Row mappers (snake_case -> camelCase app types) ────────────────────────

type Row = Record<string, unknown>

function str(v: unknown): string | undefined {
  return v === null || v === undefined ? undefined : String(v)
}
function num(v: unknown): number | undefined {
  return v === null || v === undefined ? undefined : Number(v)
}

export function mapWorkItem(row: Row, projectCode?: string): WorkItem {
  const seq = Number(row.seq ?? 0)
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    seq,
    key: deriveWorkItemKey(projectCode ?? (row.project_id as string), seq),
    itemType: row.item_type as WorkItemType,
    title: String(row.title ?? ''),
    description: str(row.description),
    status: String(row.status ?? 'backlog'),
    statusCategory: (row.status_category as StatusCategory) ?? 'todo',
    priority: (row.priority as Priority) ?? 'medium',
    moscow: (row.moscow as Moscow | null) ?? undefined,
    parentId: str(row.parent_id),
    storyPoints: num(row.story_points),
    estimateHours: num(row.estimate_hours),
    remainingHours: num(row.remaining_hours),
    loggedHours: num(row.logged_hours),
    backlogRank: str(row.backlog_rank),
    boardId: str(row.board_id),
    sprintId: str(row.sprint_id),
    programIncrementId: str(row.program_increment_id),
    assigneeId: str(row.assignee_id),
    assigneeName: str(row.assignee_name),
    reporterId: str(row.reporter_id),
    labels: Array.isArray(row.labels) ? (row.labels as string[]) : [],
    acceptanceCriteria: Array.isArray(row.acceptance_criteria)
      ? (row.acceptance_criteria as WorkItem['acceptanceCriteria'])
      : [],
    dodChecklist: Array.isArray(row.dod_checklist)
      ? (row.dod_checklist as WorkItem['dodChecklist'])
      : [],
    wbsElementId: str(row.wbs_element_id),
    controlAccountId: str(row.control_account_id),
    scheduleActivityId: str(row.schedule_activity_id),
    scheduleVersionId: str(row.schedule_version_id),
    constraintStart: str(row.constraint_start),
    constraintFinish: str(row.constraint_finish),
    evCreditRule: (row.ev_credit_rule as EvCreditRule) ?? 'zero_hundred',
    attributes:
      row.attributes && typeof row.attributes === 'object' && !Array.isArray(row.attributes)
        ? (row.attributes as Record<string, unknown>)
        : {},
    percentComplete: Number(row.percent_complete ?? 0),
    blocked: Boolean(row.blocked),
    blockedReason: str(row.blocked_reason),
    startDate: str(row.start_date),
    dueDate: str(row.due_date),
    completedAt: str(row.completed_at),
    dataClass: (row.data_class as 'demo' | 'production') ?? 'demo',
    createdAt: String(row.created_at ?? ''),
    updatedAt: String(row.updated_at ?? ''),
  }
}

function mapBoardColumn(row: Row): BoardColumn {
  return {
    id: String(row.id),
    boardId: String(row.board_id),
    name: String(row.name ?? ''),
    statusKey: String(row.status_key ?? ''),
    statusCategory: (row.status_category as StatusCategory) ?? 'todo',
    wipLimit: num(row.wip_limit),
    columnOrder: Number(row.column_order ?? 0),
  }
}

function mapBoard(row: Row, columns: BoardColumn[]): Board {
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    name: String(row.name ?? ''),
    boardType: (row.board_type as Board['boardType']) ?? 'scrum',
    swimlaneBy: (row.swimlane_by as Board['swimlaneBy']) ?? 'none',
    isDefault: Boolean(row.is_default),
    columns: [...columns].sort((a, b) => a.columnOrder - b.columnOrder),
  }
}

export function mapSprintCapacity(row: Row): SprintCapacity {
  return {
    id: String(row.id),
    sprintId: String(row.sprint_id),
    resourceId: str(row.resource_id),
    memberName: String(row.member_name ?? ''),
    capacityHours: Number(row.capacity_hours ?? 0),
  }
}

export function mapSprint(row: Row, capacity?: SprintCapacity[]): Sprint {
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    boardId: str(row.board_id),
    programIncrementId: str(row.program_increment_id),
    name: String(row.name ?? ''),
    goal: str(row.goal),
    state: (row.state as Sprint['state']) ?? 'planned',
    startDate: str(row.start_date),
    endDate: str(row.end_date),
    committedPoints: num(row.committed_points),
    completedPoints: num(row.completed_points),
    reviewNotes: str(row.review_notes),
    retroNotes: str(row.retro_notes),
    capacity,
  }
}

export function mapProgramIncrement(row: Row): ProgramIncrement {
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    name: String(row.name ?? ''),
    objective: str(row.objective),
    startDate: str(row.start_date),
    endDate: str(row.end_date),
    milestoneId: str(row.milestone_id),
    wbsElementId: str(row.wbs_element_id),
    state: (row.state as ProgramIncrement['state']) ?? 'planned',
    lifecyclePhaseTransition: str(row.lifecycle_phase_transition) ?? null,
  }
}

function mapLink(row: Row): WorkItemLink {
  return {
    id: String(row.id),
    sourceId: String(row.source_id),
    targetId: String(row.target_id),
    linkType: row.link_type as WorkItemLink['linkType'],
  }
}

function mapComment(row: Row): WorkItemComment {
  return {
    id: String(row.id),
    workItemId: String(row.work_item_id),
    authorId: str(row.author_id),
    authorName: str(row.author_name),
    body: String(row.body ?? ''),
    createdAt: String(row.created_at ?? ''),
  }
}

function mapActivity(row: Row): WorkItemActivity {
  return {
    id: String(row.id),
    workItemId: String(row.work_item_id),
    actorId: str(row.actor_id),
    actorName: str(row.actor_name),
    action: (row.action as WorkItemActivity['action']) ?? 'field_changed',
    field: str(row.field),
    fromValue: str(row.from_value),
    toValue: str(row.to_value),
    createdAt: String(row.created_at ?? ''),
  }
}

function mapSnapshot(row: Row): AgileMetricSnapshot {
  return {
    id: String(row.id),
    projectId: String(row.project_id),
    sprintId: str(row.sprint_id),
    boardId: str(row.board_id),
    snapshotDate: String(row.snapshot_date ?? ''),
    remainingPoints: num(row.remaining_points),
    completedPoints: num(row.completed_points),
    scopePoints: num(row.scope_points),
    remainingHours: num(row.remaining_hours),
    cfdCounts: (row.cfd_counts as Record<string, number> | null) ?? undefined,
    dataClass: (row.data_class as 'demo' | 'production') ?? 'demo',
  }
}

// Expose mappers for the write service (so a returned row maps consistently).
export const agileMappers = { mapWorkItem, mapSprint, mapProgramIncrement }

// ── Reads ──────────────────────────────────────────────────────────────────

export async function getWorkItems(projectId: string, projectCode?: string): Promise<WorkItem[]> {
  const { data, error } = await supabase
    .from('work_items')
    .select('*')
    .eq('project_id', projectId)
  if (error) throw new Error(`Supabase select [work_items]: ${error.message}`)
  return (data ?? []).map((r) => mapWorkItem(r as Row, projectCode))
}

export async function getBoard(projectId: string, boardId?: string): Promise<Board | null> {
  let boardQuery = supabase.from('agile_boards').select('*').eq('project_id', projectId)
  if (boardId) boardQuery = boardQuery.eq('id', boardId)
  const { data: boards, error } = await boardQuery
  if (error) throw new Error(`Supabase select [agile_boards]: ${error.message}`)
  const rows = (boards ?? []) as Row[]
  if (rows.length === 0) return null
  const chosen = rows.find((r) => r.is_default) ?? rows[0]!
  const { data: cols, error: colErr } = await supabase
    .from('board_columns')
    .select('*')
    .eq('board_id', String(chosen.id))
    .order('column_order', { ascending: true })
  if (colErr) throw new Error(`Supabase select [board_columns]: ${colErr.message}`)
  return mapBoard(chosen, (cols ?? []).map((c) => mapBoardColumn(c as Row)))
}

export async function getBoards(projectId: string): Promise<Board[]> {
  const { data: boards, error } = await supabase
    .from('agile_boards')
    .select('*')
    .eq('project_id', projectId)
  if (error) throw new Error(`Supabase select [agile_boards]: ${error.message}`)
  const boardRows = (boards ?? []) as Row[]
  if (boardRows.length === 0) return []
  const { data: cols, error: colErr } = await supabase
    .from('board_columns')
    .select('*')
    .in('board_id', boardRows.map((b) => String(b.id)))
  if (colErr) throw new Error(`Supabase select [board_columns]: ${colErr.message}`)
  const colsByBoard = new Map<string, BoardColumn[]>()
  for (const c of (cols ?? []) as Row[]) {
    const mapped = mapBoardColumn(c)
    const list = colsByBoard.get(mapped.boardId) ?? []
    list.push(mapped)
    colsByBoard.set(mapped.boardId, list)
  }
  return boardRows.map((b) => mapBoard(b, colsByBoard.get(String(b.id)) ?? []))
}

export async function getSprints(projectId: string): Promise<Sprint[]> {
  const { data, error } = await supabase
    .from('agile_sprints')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase select [agile_sprints]: ${error.message}`)
  const sprints = (data ?? []) as Row[]
  if (sprints.length === 0) return []
  const { data: caps, error: capErr } = await supabase
    .from('sprint_capacity')
    .select('*')
    .in('sprint_id', sprints.map((s) => String(s.id)))
  if (capErr) throw new Error(`Supabase select [sprint_capacity]: ${capErr.message}`)
  const capBySprint = new Map<string, SprintCapacity[]>()
  for (const c of (caps ?? []) as Row[]) {
    const mapped = mapSprintCapacity(c)
    const list = capBySprint.get(mapped.sprintId) ?? []
    list.push(mapped)
    capBySprint.set(mapped.sprintId, list)
  }
  return sprints.map((s) => mapSprint(s, capBySprint.get(String(s.id)) ?? []))
}

export async function getProgramIncrements(projectId: string): Promise<ProgramIncrement[]> {
  const { data, error } = await supabase
    .from('agile_program_increments')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: true })
  if (error) throw new Error(`Supabase select [agile_program_increments]: ${error.message}`)
  return (data ?? []).map((r) => mapProgramIncrement(r as Row))
}

export async function getWorkItemLinks(projectId: string): Promise<WorkItemLink[]> {
  // Links reference work items; scope by the project's work items.
  const { data: items, error: itemErr } = await supabase
    .from('work_items')
    .select('id')
    .eq('project_id', projectId)
  if (itemErr) throw new Error(`Supabase select [work_items ids]: ${itemErr.message}`)
  const ids = (items ?? []).map((r) => String((r as Row).id))
  if (ids.length === 0) return []
  const { data, error } = await supabase
    .from('work_item_links')
    .select('*')
    .in('source_id', ids)
  if (error) throw new Error(`Supabase select [work_item_links]: ${error.message}`)
  return (data ?? []).map((r) => mapLink(r as Row))
}

export async function getWorkItemComments(workItemId: string): Promise<WorkItemComment[]> {
  const { data, error } = await supabase
    .from('work_item_comments')
    .select('*')
    .eq('work_item_id', workItemId)
    .order('created_at', { ascending: false })
  if (error) throw new Error(`Supabase select [work_item_comments]: ${error.message}`)
  return (data ?? []).map((r) => mapComment(r as Row))
}

export async function getWorkItemActivity(workItemId: string): Promise<WorkItemActivity[]> {
  const { data, error } = await supabase
    .from('work_item_activity')
    .select('*')
    .eq('work_item_id', workItemId)
    .order('created_at', { ascending: false })
  if (error) throw new Error(`Supabase select [work_item_activity]: ${error.message}`)
  return (data ?? []).map((r) => mapActivity(r as Row))
}

export async function getMetricSnapshots(opts: {
  sprintId?: string
  boardId?: string
  projectId?: string
}): Promise<AgileMetricSnapshot[]> {
  let q = supabase.from('agile_metric_snapshots').select('*')
  if (opts.sprintId) q = q.eq('sprint_id', opts.sprintId)
  if (opts.boardId) q = q.eq('board_id', opts.boardId)
  if (opts.projectId) q = q.eq('project_id', opts.projectId)
  const { data, error } = await q.order('snapshot_date', { ascending: true })
  if (error) throw new Error(`Supabase select [agile_metric_snapshots]: ${error.message}`)
  return (data ?? []).map((r) => mapSnapshot(r as Row))
}
